import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { Button } from "@/components/ui/button";
import { Smile } from "lucide-react";
import { cn } from "@/lib/utils";
import { insertAtCursor } from "@/lib/emojiShortcuts";
import { EMOJI_PICKER_ATTR, eventFromEmojiPicker } from "@/lib/emojiPickerDom";

const PANEL_WIDTH = 352;
const PANEL_MAX_HEIGHT = 420;

function computePanelStyle(anchorRect) {
  if (!anchorRect) return { top: 0, left: 0 };
  const gap = 4;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = anchorRect.left;
  if (left + PANEL_WIDTH > vw - 8) {
    left = Math.max(8, anchorRect.right - PANEL_WIDTH);
  }
  let top = anchorRect.bottom + gap;
  const spaceBelow = vh - anchorRect.bottom - gap;
  if (spaceBelow < 200 && anchorRect.top > spaceBelow) {
    top = Math.max(8, anchorRect.top - PANEL_MAX_HEIGHT - gap);
  }
  return {
    top,
    left,
    width: PANEL_WIDTH,
    maxHeight: Math.min(PANEL_MAX_HEIGHT, Math.max(200, vh - top - 8)),
  };
}

function usePickerTheme() {
  const [theme, setTheme] = useState(() =>
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
      ? "dark"
      : "light"
  );

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => {
      setTheme(root.classList.contains("dark") ? "dark" : "light");
    };
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  return theme;
}

/**
 * Insert emoji into a controlled text value at the current caret (or append).
 * Re-exported for callers that imported from this module previously.
 */
export { insertAtCursor };

const EmojiInsertButton = ({
  value,
  onChange,
  textareaRef,
  /** Prefer this for TipTap / non-textarea composers */
  onInsert,
  disabled = false,
  className,
  /**
   * Force inline panel (inside dialogs). Auto-detected from nearest
   * [role=dialog] when omitted — body portals are blocked by RemoveScroll.
   */
  inline,
}) => {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const [useInline, setUseInline] = useState(Boolean(inline));
  const buttonRef = useRef(null);
  const theme = usePickerTheme();

  useLayoutEffect(() => {
    if (inline != null) {
      setUseInline(Boolean(inline));
      return;
    }
    setUseInline(Boolean(buttonRef.current?.closest?.('[role="dialog"]')));
  }, [inline, open]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current || useInline) {
      if (!open) setPanelStyle(null);
      return undefined;
    }
    const update = () => {
      if (!buttonRef.current) return;
      setPanelStyle(computePanelStyle(buttonRef.current.getBoundingClientRect()));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, useInline]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const handlePick = (emoji) => {
    const native = emoji?.native;
    if (!native) return;
    // Insert before closing so the editor still has focus/selection.
    if (typeof onInsert === "function") {
      onInsert(native);
    } else {
      const el = textareaRef?.current;
      const { next, caret } = insertAtCursor(value, native, el);
      onChange?.(next);
      requestAnimationFrame(() => {
        if (el) {
          el.focus();
          try {
            el.setSelectionRange(caret, caret);
          } catch {
            /* ignore */
          }
        }
      });
    }
    setOpen(false);
  };

  const picker = (
    <Picker
      data={data}
      theme={theme}
      previewPosition="none"
      skinTonePosition="search"
      onEmojiSelect={handlePick}
      onClickOutside={(e) => {
        if (buttonRef.current?.contains(e.target)) return;
        if (eventFromEmojiPicker(e)) return;
        setOpen(false);
      }}
    />
  );

  // Inside modals: keep the panel in-dialog so RemoveScroll / focus trap
  // still allow the emoji-mart search field to receive clicks and focus.
  const inlinePanel = open && useInline && (
    <div
      {...{ [EMOJI_PICKER_ATTR]: "" }}
      className="absolute left-0 top-full z-[300] mt-1 overflow-hidden overscroll-contain rounded-md border border-border bg-popover shadow-lg"
      style={{
        width: PANEL_WIDTH,
        maxWidth: "min(352px, calc(100vw - 2rem))",
        maxHeight: PANEL_MAX_HEIGHT,
      }}
      onWheel={(e) => e.stopPropagation()}
    >
      {picker}
    </div>
  );

  const portaledPanel =
    open &&
    !useInline &&
    panelStyle &&
    createPortal(
      <div
        {...{ [EMOJI_PICKER_ATTR]: "" }}
        className="pointer-events-auto fixed z-[300] overflow-hidden overscroll-contain rounded-md border border-border bg-popover shadow-lg"
        style={{
          top: panelStyle.top,
          left: panelStyle.left,
          width: panelStyle.width,
          maxHeight: panelStyle.maxHeight,
        }}
        onWheel={(e) => e.stopPropagation()}
      >
        {picker}
      </div>,
      document.body
    );

  return (
    <div className={cn("relative", className)}>
      <Button
        ref={buttonRef}
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-muted-foreground"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        disabled={disabled}
        aria-label="Insert emoji"
        title="Insert emoji"
        aria-expanded={open}
      >
        <Smile className="h-3.5 w-3.5" />
      </Button>
      {inlinePanel}
      {portaledPanel}
    </div>
  );
};

export default EmojiInsertButton;
